import type { ReactNode } from 'react';

export type WidgetRenderer<T = unknown> = (props: T) => ReactNode;

interface WidgetRegistryInstance {
  register<T>(type: string, renderer: WidgetRenderer<T>): void;

  resolve<T>(type: string): WidgetRenderer<T> | undefined;
}

function createRegistry(): WidgetRegistryInstance {
  const map = new Map<string, WidgetRenderer>();
  return {
    register(type, renderer) {
      map.set(type, renderer as WidgetRenderer);
    },
    resolve<T>(type: string) {
      return map.get(type) as WidgetRenderer<T> | undefined;
    }
  };
}

export const widgetRegistry = createRegistry();

// Widget type keys — both contributors and the host import from here.
export const WIDGET_TYPES = {
  TABLE: 'render_table',
  SPOT_CHART: 'render_spot_chart',
  SEASONAL_CHART: 'render_seasonal_chart',
  FORWARD_CHART: 'render_forward_chart',
  FILE_CHART: 'render_file_chart',
  STATIC_CHART: 'render_static_chart'
} as const;

// Shared data shape the host passes to any registered table widget renderer.
export type TableWidgetData = {
  tool_call_id: string;
  rows: Array<{
    code: string;
    title: string;
    period_type: string;
    time_ref?: number;
  }>;
  metadata?: { start_date?: string; end_date?: string };
};

export type ChartResultRow = {
  code: string;
  title: string;
  period_type: string;
  time_ref?: string | number;
  period?: string;
  group?: string;
};

export type SpotChartWidgetData = {
  tool_call_id: string;
  title?: string;
  rows: ChartResultRow[];
};

export type SeasonalChartWidgetData = {
  tool_call_id: string;
  title?: string;
  from?: string;
  rows: ChartResultRow[];
};

export type ForwardChartWidgetData = {
  tool_call_id: string;
  title?: string;
  rows: ChartResultRow[];
};

export type FileChartWidgetData = {
  tool_call_id: string;
  title?: string;
  seriesFileName: string;
  configFileName: string;
  queryFileId?: string;
  downloadFileId?: string;
  timeRangeButtons?: Array<{ label: string; value: string; default?: boolean }>;
};

export type StaticDataChartWidgetData = {
  tool_call_id: string;
  widgetId?: string;
  title?: string;
  chartConfig: Record<string, unknown>;
};
